import { reactive, toRefs } from 'vue'
import encryptionService from '@/services/encryptionService'

// Estado global del stepper
const state = reactive({
  // Paso actual (1-5)
  currentStep: 1,
  
  // Datos del Paso 1: Selección de curso y programa
  step1: {
    year: '4',         // Año escolar ('1'-'4') o 'e' para egresado
    modalidad: null,   // Modalidad seleccionada
    program: null,     // Programa seleccionado
    completed: false   // Si el paso está completado
  },
  
  // Datos del Paso 2: Datos personales
  step2: {
    firstName: '',           // Nombre del estudiante
    firstLastName: '',       // Primer apellido
    secondLastName: '',      // Segundo apellido
    rut: '',                // RUT
    email: '',              // Email
    phoneNumber: '',        // Celular del estudiante
    birthDate: '',          // Fecha de nacimiento
    preferredLocation: null, // Sede de preferencia
    selectedProgram: '',     // Programa seleccionado
    selectedProgramId: null, // ID del programa seleccionado
    isFormValid: false,      // Si el formulario es válido
    studentId: null,         // <--- AGREGADO
    orderId: null,           // <--- AGREGADO
    completed: false
  },
  
  // Datos del Paso 3: Selección de sede, mes y cursos
  step3: {
    selectedComuna: null,      // Comuna seleccionada
    selectedComunaName: null,  // Nombre de la comuna
    selectedSede: null,        // Sede seleccionada
    selectedSedeName: null,    // Nombre de la sede
    selectedMonth: null,       // Mes seleccionado
    selectedCourses: [],       // Cursos seleccionados (resumen)
    selectedCoursesRaw: [],    // Cursos seleccionados (raw para calendario)
    leftColumnMaterias: [],    // Materias columna izquierda
    rightColumnMaterias: [],   // Materias columna derecha
    isFormValid: false,        // Si el formulario es válido
    completed: false
  },
  
  // Datos del Paso 4: Confirmación de datos
  step4: {
    coupon: null,       // Cupón aplicado
    confirmed: false,  // Si los datos fueron confirmados
    quote: null,       // Respuesta de la API de cotización
    completed: false
  },
  
  // Datos del Paso 5: Información personal
  step5: {
    apoderadoCompleted: false,  // Si el apoderado está completado
    studentCompleted: false,     // Si el estudiante está completado
    financingPartyData: {},      // Datos del apoderado
    emailValidated: false,       // Si el email está validado
    completed: false
  }
})

// Métodos para manejar el estado
const actions = {
  // Navegación entre pasos
  setCurrentStep(step) {
    if (step >= 1 && step <= 5) {
      state.currentStep = step
    }
  },
  
  nextStep() {
    if (state.currentStep < 5) {
      state.currentStep++
    }
  },
  
  previousStep() {
    if (state.currentStep > 1) {
      state.currentStep--
    }
  },
  
  // Actualizar datos del Paso 1
  updateStep1(data) {
    // Asegurar que year sea string
    if (data.year !== undefined) {
      data.year = String(data.year)
    }
    Object.assign(state.step1, data)
    state.step1.completed = !!(state.step1.year && state.step1.modalidad && state.step1.program)
  },
  
  // Actualizar datos del Paso 2
  updateStep2(data) {
    Object.assign(state.step2, data)
    state.step2.completed = !!(state.step2.firstName && state.step2.firstLastName && state.step2.secondLastName && state.step2.rut && state.step2.email && state.step2.phoneNumber && state.step2.birthDate && state.step2.preferredLocation && state.step2.selectedProgram && state.step2.isFormValid)
  },
  
  // Actualizar datos del Paso 3
  updateStep3(data) {
    Object.assign(state.step3, data)
    state.step3.completed = !!(state.step3.selectedComuna && state.step3.selectedSede && state.step3.selectedMonth && state.step3.selectedCourses.length > 0 && state.step3.isFormValid)
  },
  
  // Actualizar datos del Paso 4
  updateStep4(data) {
    Object.assign(state.step4, data)
    state.step4.completed = state.step4.confirmed
  },
  
  // Establecer cupón
  setCoupon(coupon) {
    state.step4.coupon = coupon
  },
  
  // Actualizar datos del Paso 5
  updateStep5(data) {
    Object.assign(state.step5, data)
    // Validar que el paso esté completado basado en apoderadoCompleted y studentCompleted
    state.step5.completed = !!(state.step5.apoderadoCompleted && state.step5.studentCompleted)
  },
  
  // Obtener todos los datos del stepper
  getAllData() {
    return {
      step1: { ...state.step1 },
      step2: { ...state.step2 },
      step3: { ...state.step3 },
      step4: { ...state.step4 },
      step5: { ...state.step5 }
    }
  },
  
  // Verificar si un paso específico está completado
  isStepCompleted(step) {
    switch (step) {
      case 1: return state.step1.completed
      case 2: return state.step2.completed
      case 3: return state.step3.completed
      case 4: return state.step4.completed
      case 5: return state.step5.completed
      default: return false
    }
  },
  
  // Verificar si se puede continuar al siguiente paso
  canContinueToNextStep() {
    return this.isStepCompleted(state.currentStep)
  },
  
  // Resetear todos los datos
  resetAll() {
    state.currentStep = 1
    state.step1 = { year: 4, modalidad: null, program: null, completed: false }
    state.step2 = { firstName: '', firstLastName: '', secondLastName: '', rut: '', email: '', phoneNumber: '', preferredLocation: null, selectedProgram: '', isFormValid: false, studentId: null, orderId: null, completed: false, studentData: {}, orderData: {}, appliedCoupon: null }
    state.step3 = { selectedComuna: null, selectedComunaName: null, selectedSede: null, selectedSedeName: null, selectedMonth: null, selectedCourses: [], selectedCoursesRaw: [], leftColumnMaterias: [], rightColumnMaterias: [], isFormValid: false, completed: false }
    state.step4 = { coupon: null, confirmed: false, quote: null, completed: false }
    state.step5 = { apoderadoCompleted: false, studentCompleted: false, financingPartyData: {}, emailValidated: false, completed: false }
    localStorage.removeItem('stepperData')
    localStorage.removeItem('stepperCurrentStep')
  },
  
  // Guardar en localStorage (encriptado)
  saveToLocalStorage() {
    const allData = this.getAllData()
    const encryptedData = encryptionService.encryptObject(allData)
    localStorage.setItem('stepperData', JSON.stringify(encryptedData))
    localStorage.setItem('stepperCurrentStep', state.currentStep.toString())
  },
  
  // Cargar desde localStorage (desencriptado)
  loadFromLocalStorage() {
    try {
      const savedData = localStorage.getItem('stepperData')
      const savedStep = localStorage.getItem('stepperCurrentStep')
      
      if (savedData) {
        const encryptedData = JSON.parse(savedData)
        const data = encryptionService.decryptObject(encryptedData)
        Object.assign(state.step1, data.step1 || {})
        Object.assign(state.step2, data.step2 || {})
        Object.assign(state.step3, data.step3 || {})
        Object.assign(state.step4, data.step4 || {})
        Object.assign(state.step5, data.step5 || {})
      }
      
      if (savedStep) {
        state.currentStep = parseInt(savedStep)
      }
    } catch (error) {
      console.error('Error loading stepper data from localStorage:', error)
    }
  },

  // Obtener valor de un campo específico (encriptado o desencriptado)
  getFieldValue(fieldPath, encrypted = false) {
    const allData = this.getAllData()
    return encryptionService.getFieldValue(allData, fieldPath, encrypted)
  },

  // Establecer valor de un campo específico (encriptado)
  setFieldValue(fieldPath, value) {
    const allData = this.getAllData()
    const updatedData = encryptionService.setFieldValue(allData, fieldPath, value)
    
    // Actualizar el estado con los datos modificados
    if (updatedData.step1) Object.assign(state.step1, updatedData.step1)
    if (updatedData.step2) Object.assign(state.step2, updatedData.step2)
    if (updatedData.step3) Object.assign(state.step3, updatedData.step3)
    if (updatedData.step4) Object.assign(state.step4, updatedData.step4)
    if (updatedData.step5) Object.assign(state.step5, updatedData.step5)
  },

  // Obtener datos encriptados para envío
  getEncryptedData() {
    return encryptionService.encryptObject(this.getAllData())
  },

  // Obtener datos desencriptados para uso interno
  getDecryptedData() {
    return this.getAllData()
  },

  // Verificar estado de encriptación
  isEncryptionEnabled() {
    return encryptionService.encryptionEnabled
  },

  // Obtener información de configuración
  getEncryptionInfo() {
    return {
      enabled: encryptionService.encryptionEnabled,
      hasKey: !!encryptionService.secretKey,
      keyLength: encryptionService.secretKey?.length || 0
    }
  }
}

// Exportar el store
export const useStepperStore = () => {
  return {
    ...toRefs(state),
    ...actions
  }
}

// Exportar el estado y acciones por separado para compatibilidad
export { state as stepperState, actions as stepperActions } 